import {
  appointmentListResponseSchema,
  appointmentResponseSchema,
  availabilityResponseSchema,
  orgMemberListResponseSchema,
  type Appointment,
  type AvailabilityQuery,
  type OrgMember,
  type PatientPortalCreateAppointmentRequest,
  type TimeRange,
} from '@emr/shared';

import { apiRequest } from '../client.ts';

/**
 * The patient portal's self-scheduling surface (`BACKLOG.md` W2 pillar 2, plus its `W5` follow-up,
 * `server/src/patientScheduling/routes.ts`). `providers`/`availability` are unaudited reads
 * server-side (that route file's own header explains why) but still session-gated; nothing about
 * that changes how this client calls them. `listMyAppointments` reuses `@emr/shared`'s
 * `appointmentListResponseSchema` wholesale — the identical wire shape the staff
 * `GET /api/appointments` client already validates against, since the server route reuses
 * `scheduling/service.ts`'s own `Appointment` row mapping too.
 */

export async function listBookableProviders(signal?: AbortSignal): Promise<readonly OrgMember[]> {
  const response = await apiRequest(
    '/api/patient-portal/scheduling/providers',
    orgMemberListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.members;
}

export async function getProviderAvailability(
  query: AvailabilityQuery,
  signal?: AbortSignal,
): Promise<readonly TimeRange[]> {
  const params = new URLSearchParams({
    providerId: query.providerId,
    from: query.from,
    to: query.to,
  });
  const response = await apiRequest(
    `/api/patient-portal/scheduling/availability?${params.toString()}`,
    availabilityResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.ranges;
}

export async function bookMyAppointment(
  input: PatientPortalCreateAppointmentRequest,
): Promise<Appointment> {
  const response = await apiRequest(
    '/api/patient-portal/scheduling/appointments',
    appointmentResponseSchema,
    { method: 'POST', body: input },
  );
  return response.appointment;
}

/** `BACKLOG.md` W5: every appointment the caller has ever booked, ordered oldest-first — see
 * `server/src/patientScheduling/routes.ts`'s own header for why this is audited (unlike
 * `listBookableProviders`/`getProviderAvailability` above). */
export async function listMyAppointments(signal?: AbortSignal): Promise<readonly Appointment[]> {
  const response = await apiRequest(
    '/api/patient-portal/scheduling/appointments',
    appointmentListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.appointments;
}
